import { useState, useRef, useCallback } from 'react';
import Modal from './Modal';
import Icon  from './Icon';
import { Storage } from '../storage';

// ─── CSV IMPORT MODAL ─────────────────────────────────────────────────────────
//
//  Props:
//    open         {boolean}
//    onClose      {function}
//    timelineId   {string}
//    isMapLayout  {boolean}        show map-specific column reference + template
//    onImported   {function(result)}  called with server response on success
//

const PREVIEW_ROWS = 5;

// Column reference shown in the map-layout import modal. Keep in sync with
// the server's CSV parser (server/server.js → /api/timelines/:id/import/csv).
const MAP_CSV_COLUMNS = [
  { name: 'title',          required: true,  type: 'string',  desc: 'Event title' },
  { name: 'startDate',      required: true,  type: 'date',    desc: 'YYYY-MM-DD (negative year for BC, e.g. -0027 = 27 BC)' },
  { name: 'endDate',        required: false, type: 'date',    desc: 'YYYY-MM-DD' },
  { name: 'description',    required: false, type: 'string',  desc: 'Plain-text description' },
  { name: 'lat',            required: false, type: 'number',  desc: 'Latitude (-90 to 90). Must be paired with lng.' },
  { name: 'lng',            required: false, type: 'number',  desc: 'Longitude (-180 to 180). Must be paired with lat.' },
  { name: 'location',       required: false, type: 'string',  desc: 'Human-readable location label (e.g. "Londinium, Britannia")' },
  { name: 'mapIcon',        required: false, type: 'string',  desc: 'pin · sword · crown · column · skull · flame · ship · book · coin · star · flag · person' },
  { name: 'mapMarkerSize',  required: false, type: 'string',  desc: 'small · medium · large (default: medium)' },
  { name: 'tags',           required: false, type: 'string',  desc: 'Comma-separated, e.g. "battle,naval,augustus"' },
  { name: 'photo',          required: false, type: 'string',  desc: 'Public https URL — Supabase Storage URLs from this project are ideal' },
];

const CSVImportModal = ({ open, onClose, timelineId, isMapLayout = false, onImported }) => {
  const [file,        setFile]        = useState(null);
  const [parsedRows,  setParsedRows]  = useState(null);  // parsed locally for preview
  const [dragOver,    setDragOver]    = useState(false);
  const [importing,   setImporting]   = useState(false);
  const [result,      setResult]      = useState(null);   // server response after import
  const [error,       setError]       = useState('');
  const fileInputRef  = useRef(null);

  const reset = () => {
    setFile(null);
    setParsedRows(null);
    setResult(null);
    setError('');
  };

  const handleClose = () => {
    if (importing) return;
    reset();
    onClose();
  };

  // ── Parse CSV client-side for preview ──────────────────────────────────────
  const parsePreview = (text) => {
    const lines = text.split(/\r?\n/).filter(l => l.trim());
    if (!lines.length) return null;

    // Simple CSV split — handles quoted fields
    const splitLine = (line) => {
      const cells = [];
      let cur = '';
      let inQ  = false;
      for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') { inQ = !inQ; continue; }
        if (ch === ',' && !inQ) { cells.push(cur.trim()); cur = ''; continue; }
        cur += ch;
      }
      cells.push(cur.trim());
      return cells;
    };

    const headers = splitLine(lines[0]);
    const rows    = lines.slice(1, PREVIEW_ROWS + 1).map(l => splitLine(l));
    const total   = lines.length - 1; // subtract header
    return { headers, rows, total };
  };

  const handleFileAccepted = (f) => {
    setError('');
    setResult(null);
    if (!f.name.toLowerCase().endsWith('.csv')) {
      setError('Only .csv files are supported.');
      return;
    }
    if (f.size > 2 * 1024 * 1024) {
      setError('File exceeds the 2 MB size limit.');
      return;
    }
    setFile(f);
    const reader = new FileReader();
    reader.onload = (e) => {
      const preview = parsePreview(e.target.result || '');
      setParsedRows(preview);
    };
    reader.readAsText(f);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragOver(false);
    const f = e.dataTransfer.files[0];
    if (f) handleFileAccepted(f);
  }, []);

  const handleDragOver  = (e) => { e.preventDefault(); setDragOver(true);  };
  const handleDragLeave = ()  => setDragOver(false);

  const handleFileInput = (e) => {
    const f = e.target.files[0];
    if (f) handleFileAccepted(f);
    e.target.value = '';
  };

  // ── Import ──────────────────────────────────────────────────────────────────
  const handleImport = async () => {
    if (!file || importing) return;
    setImporting(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await Storage.importEventsFromCSV(timelineId, fd);
      setResult(res);
      onImported(res);
    } catch (err) {
      setError(err.message || 'Import failed.');
    } finally {
      setImporting(false);
    }
  };

  const handleTemplate = () => Storage.downloadCSVTemplate(isMapLayout ? 'map' : null);

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Modal open={open} onClose={handleClose} title="Import Events from CSV" width={680}>

      {/* ── Template download hint ── */}
      <div className="imp-hint">
        <Icon name="info" size={14} />
        <span>
          Need a template?{' '}
          <button className="imp-link" onClick={handleTemplate}>
            {isMapLayout ? 'Download map CSV template' : 'Download CSV template'}
          </button>
          {isMapLayout && ' (Roman Empire example with all map fields)'}
        </span>
      </div>

      {/* ── Map-specific column reference ── */}
      {isMapLayout && !result && (
        <details className="imp-coldoc">
          <summary>Supported columns for map timelines</summary>
          <table className="imp-coldoc-table">
            <thead>
              <tr>
                <th>Column</th><th>Type</th><th>Required</th><th>Description</th>
              </tr>
            </thead>
            <tbody>
              {MAP_CSV_COLUMNS.map(c => (
                <tr key={c.name}>
                  <td><code>{c.name}</code></td>
                  <td>{c.type}</td>
                  <td>{c.required ? 'required' : 'optional'}</td>
                  <td>{c.desc}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="imp-coldoc-note">
            lat and lng must be provided together. Photo URLs must be publicly
            accessible https links — the importer stores the URL directly and
            does not re-host the image.
          </p>
        </details>
      )}

      {/* ── Drop zone ── */}
      {!result && (
        <div
          className={`imp-dropzone${dragOver ? ' imp-dropzone--over' : ''}${file ? ' imp-dropzone--has-file' : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => !file && fileInputRef.current?.click()}
          role="button"
          tabIndex={0}
          aria-label="Drop CSV file here or click to browse"
          onKeyDown={(e) => e.key === 'Enter' && !file && fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            style={{ display: 'none' }}
            onChange={handleFileInput}
          />
          {file ? (
            <div className="imp-file-selected">
              <Icon name="document" size={20} />
              <span className="imp-filename">{file.name}</span>
              <button
                className="imp-remove-file"
                onClick={(e) => { e.stopPropagation(); reset(); }}
                aria-label="Remove file"
              >
                <Icon name="close" size={14} />
              </button>
            </div>
          ) : (
            <>
              <Icon name="upload" size={28} />
              <p className="imp-drop-label">Drop your CSV here or <span className="imp-link">browse</span></p>
              <p className="imp-drop-sub">Max 2 MB · .csv only</p>
            </>
          )}
        </div>
      )}

      {/* ── Error ── */}
      {error && (
        <div className="imp-error">
          <Icon name="warning" size={14} />
          {error}
        </div>
      )}

      {/* ── Large-file warning ── */}
      {parsedRows && parsedRows.total > 200 && !result && (
        <div className="imp-warn">
          <Icon name="warning" size={14} />
          <span>
            <strong>{parsedRows.total.toLocaleString()} rows</strong> detected. Large imports may take a moment.
          </span>
        </div>
      )}

      {/* ── Preview table ── */}
      {parsedRows && !result && (
        <div className="imp-preview">
          <p className="imp-preview-label">
            Preview — first {Math.min(PREVIEW_ROWS, parsedRows.rows.length)} of {parsedRows.total.toLocaleString()} row{parsedRows.total !== 1 ? 's' : ''}
          </p>
          <p className="imp-detected">
            <strong>Detected columns:</strong> {parsedRows.headers.filter(Boolean).join(', ')}
          </p>
          <div className="imp-table-wrap">
            <table className="imp-table">
              <thead>
                <tr>{parsedRows.headers.map((h, i) => <th key={i}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {parsedRows.rows.map((row, ri) => (
                  <tr key={ri}>{row.map((cell, ci) => <td key={ci}>{cell}</td>)}</tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Result summary (shown after a successful import) ── */}
      {result && (() => {
        // Split warnings into row-level (have a numeric row prop) and duplicate
        // skips (no row prop), so the import summary can list them distinctly.
        const coordWarnings = (result.warnings || []).filter(w => /invalid (lat|lng)|skipped coordinates|coordinates/i.test(w.message || ''));
        const iconWarnings  = (result.warnings || []).filter(w => /mapIcon|unknown mapIcon/i.test(w.message || ''));
        const dupWarnings   = (result.warnings || []).filter(w => /duplicate/i.test(w.message || ''));
        const otherWarnings = (result.warnings || []).filter(w =>
          !coordWarnings.includes(w) && !iconWarnings.includes(w) && !dupWarnings.includes(w)
        );
        // Aggregate sentence — "X events imported. Y warnings: rows … had …"
        const coordRows = coordWarnings.map(w => w.row).filter(Boolean);
        const sentenceParts = [];
        sentenceParts.push(`${result.imported} event${result.imported !== 1 ? 's' : ''} imported successfully.`);
        if (coordRows.length) {
          sentenceParts.push(`${coordRows.length} warning${coordRows.length !== 1 ? 's' : ''}: row${coordRows.length !== 1 ? 's' : ''} ${coordRows.join(', ')} had invalid coordinates and were imported without map pins.`);
        }
        return (
          <div className="imp-result">
            <div className="imp-result-stats">
              <div className="imp-stat imp-stat--ok">
                <span className="imp-stat-num">{result.imported}</span>
                <span className="imp-stat-lbl">imported</span>
              </div>
              {dupWarnings.length > 0 && (
                <div className="imp-stat imp-stat--warn">
                  <span className="imp-stat-num">{dupWarnings.length}</span>
                  <span className="imp-stat-lbl">skipped (duplicates)</span>
                </div>
              )}
              {(coordWarnings.length + iconWarnings.length + otherWarnings.length) > 0 && (
                <div className="imp-stat imp-stat--warn">
                  <span className="imp-stat-num">{coordWarnings.length + iconWarnings.length + otherWarnings.length}</span>
                  <span className="imp-stat-lbl">warnings</span>
                </div>
              )}
              {result.errors.length > 0 && (
                <div className="imp-stat imp-stat--err">
                  <span className="imp-stat-num">{result.errors.length}</span>
                  <span className="imp-stat-lbl">errors</span>
                </div>
              )}
            </div>

            {coordRows.length > 0 && (
              <p className="imp-result-summary">{sentenceParts.join(' ')}</p>
            )}

            {isMapLayout && result.detectedColumns && result.detectedColumns.length > 0 && (
              <p className="imp-detected">
                <strong>Detected columns:</strong> {result.detectedColumns.join(', ')}
              </p>
            )}

            {result.errors.length > 0 && (
              <div className="imp-msg-list">
                <p className="imp-msg-list-title">Errors</p>
                {result.errors.slice(0, 10).map((e, i) => (
                  <p key={i} className="imp-msg-item imp-msg-item--err">Row {e.row}: {e.message}</p>
                ))}
                {result.errors.length > 10 && (
                  <p className="imp-msg-item imp-msg-item--err">…and {result.errors.length - 10} more</p>
                )}
              </div>
            )}

            {(coordWarnings.length + iconWarnings.length + otherWarnings.length) > 0 && (
              <div className="imp-msg-list">
                <p className="imp-msg-list-title">Warnings</p>
                {[...coordWarnings, ...iconWarnings, ...otherWarnings].slice(0, 10).map((w, i) => (
                  <p key={i} className="imp-msg-item imp-msg-item--warn">{w.message}</p>
                ))}
                {(coordWarnings.length + iconWarnings.length + otherWarnings.length) > 10 && (
                  <p className="imp-msg-item imp-msg-item--warn">…and {(coordWarnings.length + iconWarnings.length + otherWarnings.length) - 10} more</p>
                )}
              </div>
            )}

            {dupWarnings.length > 0 && (
              <div className="imp-msg-list">
                <p className="imp-msg-list-title">Skipped (duplicates)</p>
                {dupWarnings.slice(0, 5).map((w, i) => (
                  <p key={i} className="imp-msg-item imp-msg-item--warn">{w.message}</p>
                ))}
                {dupWarnings.length > 5 && (
                  <p className="imp-msg-item imp-msg-item--warn">…and {dupWarnings.length - 5} more</p>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* ── Actions ── */}
      <div className="imp-actions">
        {result ? (
          <>
            <button className="btn-secondary" onClick={() => { reset(); }}>
              Import Another
            </button>
            <button className="btn-primary" onClick={handleClose}>
              Done
            </button>
          </>
        ) : (
          <>
            <button className="btn-secondary" onClick={handleClose} disabled={importing}>
              Cancel
            </button>
            <button
              className="btn-primary"
              onClick={handleImport}
              disabled={!file || importing}
            >
              {importing ? 'Importing…' : `Import${parsedRows ? ` ${parsedRows.total.toLocaleString()} row${parsedRows.total !== 1 ? 's' : ''}` : ''}`}
            </button>
          </>
        )}
      </div>
    </Modal>
  );
};

export default CSVImportModal;
